import { useQuery } from '@tanstack/react-query'
import { usersApi } from '@/api/users'
import { RoleNames } from '@/lib/permissions'
import { useTenant } from '@/contexts/TenantContext'

export function useCurrentUser() {
  const { currentTenant } = useTenant()
  const tenantId = currentTenant?.id

  return useQuery({
    queryKey: ['users', 'me', tenantId] as const,
    queryFn: usersApi.me,
    enabled: !!tenantId,
  })
}

function useProjectAccess(projectId: string | undefined) {
  const { currentTenant } = useTenant()
  const tenantId = currentTenant?.id

  return useQuery({
    queryKey: ['users', 'me', 'project-access', tenantId, projectId] as const,
    queryFn: () => usersApi.meProjectAccess(projectId!),
    enabled: !!tenantId && !!projectId,
  })
}

export function useAuth(projectId?: string) {
  const { data: user, isLoading: userQueryLoading, isError: userIsError } = useCurrentUser()
  const { data: projectAccess, isLoading: accessQueryLoading } = useProjectAccess(projectId)

  const userLoading = userQueryLoading || (!user && !userIsError)
  const accessLoading = !!projectId && accessQueryLoading
  const isLoading = userLoading || accessLoading

  const hasPermission = (permission: string, pid?: string) => {
    if (!user) return false

    if (permission.startsWith('Host.'))
      return user.hostPermissions?.includes(permission) ?? false

    const targetProjectId = pid ?? projectId
    if (!targetProjectId) return false
    if (projectAccess?.projectId !== targetProjectId) return false

    return projectAccess.permissions.includes(permission)
  }

  const hasRole = (role: string) =>
    user?.roles?.includes(role) ?? false

  const getProjectRoles = (pid?: string): string[] => {
    const targetProjectId = pid ?? projectId
    if (!targetProjectId) return []
    if (projectAccess?.projectId !== targetProjectId) return []
    return projectAccess.roles
  }

  const isAdmin = hasRole(RoleNames.Admin)

  return {
    user,
    isLoading,
    hasPermission,
    hasRole,
    getProjectRoles,
    isAdmin,
  }
}
